/**
 * Owner: Masa Bayu
 * File: typography-resolve.ts
 * Date:Time: 2026-09-19 18:07
 * Description: Resolve role-stable typography stacks per script (GL-06).
 */
import { QxLanguageError } from './errors';
import { resolveLanguageScriptRelation } from './script-relation';
import { isQxLanguageTypographyRole, } from './typography-codes';
import { resolveScriptFontDefaults } from './typography-registry';
const FORBIDDEN_FACE_MARKERS = [
    'css',
    'url(',
    '.woff',
    '.ttf',
    '.otf',
    'font-family:',
];
export function assertOpaqueFontFamilyId(fontFamilyId) {
    const trimmed = fontFamilyId.trim();
    if (trimmed.length === 0 || /\s/.test(trimmed)) {
        throw new QxLanguageError('INVALID_INPUT', `fontFamilyId must be a non-empty opaque id without whitespace: ${fontFamilyId}`);
    }
    const lower = trimmed.toLowerCase();
    for (const marker of FORBIDDEN_FACE_MARKERS) {
        if (lower.includes(marker)) {
            throw new QxLanguageError('INVALID_INPUT', `fontFamilyId must not be a CSS/file face (${marker}): ${fontFamilyId}`);
        }
    }
    // Forbidden: inventing tofu / missing-glyph asset ids
    if (lower === 'tofu' || lower === 'missing-glyph' || lower === '□') {
        throw new QxLanguageError('INVALID_INPUT', 'fontFamilyId must not invent a missing-glyph / tofu asset id');
    }
    return trimmed;
}
/**
 * Same role → script-specific stack. Does not create roles like "arabic-body".
 */
export function resolveTypographyForScript(input) {
    if (!isQxLanguageTypographyRole(input.role)) {
        throw new QxLanguageError('INVALID_INPUT', `unknown typography role (must match QXTypographySystem): ${String(input.role)}`);
    }
    let scriptId = input.scriptId;
    if (scriptId === undefined) {
        if (input.languageId === undefined) {
            throw new QxLanguageError('INVALID_INPUT', 'resolveTypographyForScript requires scriptId or languageId');
        }
        const relation = resolveLanguageScriptRelation(input.languageId);
        if (!relation.resolvedScriptId) {
            throw new QxLanguageError('NOT_FOUND', `cannot resolve script for languageId: ${input.languageId}`);
        }
        scriptId = relation.resolvedScriptId;
    }
    const defaults = resolveScriptFontDefaults(scriptId);
    const authorId = input.authorFontFamilyId !== undefined
        ? assertOpaqueFontFamilyId(input.authorFontFamilyId)
        : null;
    const themeId = input.themeFontFamilyId !== undefined
        ? assertOpaqueFontFamilyId(input.themeFontFamilyId)
        : null;
    const chain = [];
    if (authorId) {
        chain.push(Object.freeze({ slot: 'author', fontFamilyId: authorId }));
    }
    if (themeId) {
        chain.push(Object.freeze({ slot: 'theme', fontFamilyId: themeId }));
    }
    chain.push(Object.freeze({
        slot: 'script_fallback',
        fontFamilyId: defaults.defaultFontFamilyId,
    }));
    for (const id of defaults.fallbackFontFamilyIds) {
        const slot = id.startsWith('platform-') ? 'platform' : id.startsWith('last-resort') ? 'last_resort' : 'script_fallback';
        if (!chain.some((entry) => entry.fontFamilyId === id)) {
            chain.push(Object.freeze({ slot, fontFamilyId: id }));
        }
    }
    if (!chain.some((entry) => entry.slot === 'last_resort')) {
        chain.push(Object.freeze({
            slot: 'last_resort',
            fontFamilyId: 'last-resort-readable',
        }));
    }
    const primary = authorId ?? themeId ?? defaults.defaultFontFamilyId;
    return Object.freeze({
        scriptId: defaults.scriptId,
        role: input.role,
        primaryFontFamilyId: primary,
        fallbackChain: Object.freeze(chain),
        shapingRequired: defaults.shapingRequired,
        authorPreferredApplied: authorId !== null,
    });
}
//# sourceMappingURL=typography-resolve.js.map